/**
 * SavedReportTemplatesPage — two-pane UI for saved report templates.
 * Left: list of saved templates with row actions (Run, Edit, Duplicate, Delete)
 *        + "New Template" button that opens the create form.
 * Right: preview panel that renders the selected template's definition +
 *        last-run summary.
 * Ported (modernized) from apps/dash-legacy/src/routes/_authed/reports/saved.tsx.
 * Bound only to /api/v1/saved-report-templates via ./hooks.
 */
import { useMemo, useState } from "react";
import {
	useSavedReportTemplatesCreate,
	useSavedReportTemplatesDelete,
	useSavedReportTemplatesList,
} from "./hooks";
import type {
	ReportTemplateDefinition,
	SavedReportTemplateRow,
} from "./api";

type FormMode =
	| { kind: "hidden" }
	| { kind: "new" }
	| { kind: "edit"; template: SavedReportTemplateRow }
	| { kind: "duplicate"; template: SavedReportTemplateRow };

function relativeTime(ts?: number | null): string {
	if (!ts) return "Never";
	const now = Date.now() / 1000;
	const diff = Math.max(0, now - ts);
	if (diff < 60) return "just now";
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
	return new Date(ts * 1000).toISOString().slice(0, 10);
}

function summarizeDefinition(def: ReportTemplateDefinition | undefined): string {
	const groups = (def?.group_by ?? []).slice(0, 3).join(", ");
	const metrics = (def?.metrics ?? []).length;
	const reportType = def?.report_type ?? "—";
	return `${reportType} · Grouped by ${groups || "—"} · ${metrics} metric${metrics === 1 ? "" : "s"}`;
}

const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-emerald-500 to-sky-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:from-emerald-600 hover:to-sky-600 disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_GHOST =
	"inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_DANGER =
	"inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed";
const INPUT =
	"block w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400";
const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm";

export function SavedReportTemplatesPage() {
	const listQuery = useSavedReportTemplatesList();
	const createMutation = useSavedReportTemplatesCreate();
	const deleteMutation = useSavedReportTemplatesDelete();

	const [query, setQuery] = useState("");
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [formMode, setFormMode] = useState<FormMode>({ kind: "hidden" });

	const all = listQuery.data?.items ?? [];

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		if (!needle) return all;
		return all.filter((r) => r.name.toLowerCase().includes(needle));
	}, [all, query]);

	const selected = useMemo(
		() => all.find((r) => r.id === selectedId) ?? null,
		[all, selectedId],
	);

	async function handleDelete(row: SavedReportTemplateRow) {
		if (
			typeof window !== "undefined" &&
			typeof window.confirm === "function"
		) {
			if (!window.confirm(`Delete template "${row.name}"?`)) return;
		}
		await deleteMutation.mutateAsync(row.id);
		if (selectedId === row.id) setSelectedId(null);
	}

	function handleRun(row: SavedReportTemplateRow) {
		setSelectedId(row.id);
		setFormMode({ kind: "hidden" });
	}

	return (
		<main
			data-testid="saved-report-templates-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8"
		>
			<header className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Reports
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Saved Report Templates
					</h1>
				</div>
				<button
					type="button"
					className={BTN_PRIMARY}
					data-testid="saved-report-templates-new-button"
					onClick={() => {
						setFormMode({ kind: "new" });
						setSelectedId(null);
					}}
				>
					New Template
				</button>
			</header>

			<div
				className={`${CARD} flex items-center gap-2 px-3 py-2`}
				role="search"
			>
				<input
					type="search"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search saved templates"
					className={`${INPUT} border-0 shadow-none focus:ring-0`}
					aria-label="Search saved report templates"
				/>
				<span className="whitespace-nowrap px-2 text-xs text-zinc-500">
					{filtered.length} of {all.length}
				</span>
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
				<section
					className={`${CARD} lg:col-span-3`}
					aria-label="Saved templates list"
				>
					<div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
						<h2 className="text-sm font-semibold text-zinc-900">
							Templates
						</h2>
						<span className="text-xs text-zinc-500">
							{listQuery.isLoading ? "Loading…" : `${all.length} total`}
						</span>
					</div>

					{listQuery.isLoading ? (
						<p
							className="px-4 py-8 text-sm text-zinc-500"
							data-testid="saved-report-templates-loading"
						>
							Loading.
						</p>
					) : listQuery.error ? (
						<p
							className="px-4 py-8 text-sm text-red-600"
							role="alert"
							data-testid="saved-report-templates-error"
						>
							{listQuery.error.message}
						</p>
					) : filtered.length === 0 ? (
						<div
							className="flex flex-col items-center justify-center gap-2 px-4 py-14 text-center"
							data-testid="saved-report-templates-empty"
						>
							<div
								aria-hidden
								className="mb-2 h-12 w-12 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100"
							/>
							<h3 className="text-base font-semibold text-zinc-900">
								{all.length === 0
									? "No saved templates yet"
									: "No templates match your search"}
							</h3>
							{all.length === 0 ? (
								<>
									<p className="max-w-sm text-sm text-zinc-500">
										Save a report template to run the same view any time.
									</p>
									<button
										type="button"
										className={`${BTN_PRIMARY} mt-2`}
										onClick={() => setFormMode({ kind: "new" })}
									>
										Create your first template
									</button>
								</>
							) : null}
						</div>
					) : (
						<div className="overflow-x-auto">
							<table
								className="w-full border-collapse text-sm"
								data-testid="saved-report-templates-table"
							>
								<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
									<tr>
										<th className="px-4 py-2.5">Name</th>
										<th className="px-4 py-2.5">Definition</th>
										<th className="px-4 py-2.5">Last run</th>
										<th className="px-4 py-2.5 text-right">Actions</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-100">
									{filtered.map((row) => {
										const isSelected = row.id === selectedId;
										return (
											<tr
												key={row.id}
												data-testid={`saved-report-templates-row-${row.id}`}
												className={
													isSelected
														? "bg-emerald-50/40"
														: "hover:bg-zinc-50"
												}
											>
												<td className="px-4 py-2.5">
													<button
														type="button"
														className="text-left font-medium text-zinc-900 hover:text-emerald-600"
														onClick={() => handleRun(row)}
													>
														{row.name}
													</button>
												</td>
												<td className="px-4 py-2.5 text-xs text-zinc-500">
													{summarizeDefinition(row.definition)}
												</td>
												<td className="px-4 py-2.5 text-xs text-zinc-500">
													{relativeTime(row.last_run_at)}
												</td>
												<td className="px-4 py-2.5 text-right">
													<div className="flex items-center justify-end gap-1">
														<button
															type="button"
															className={BTN_GHOST}
															onClick={() => handleRun(row)}
															data-testid={`saved-report-templates-run-${row.id}`}
														>
															Run
														</button>
														<button
															type="button"
															className={BTN_GHOST}
															onClick={() =>
																setFormMode({ kind: "edit", template: row })
															}
														>
															Edit
														</button>
														<button
															type="button"
															className={BTN_GHOST}
															onClick={() =>
																setFormMode({
																	kind: "duplicate",
																	template: row,
																})
															}
														>
															Duplicate
														</button>
														<button
															type="button"
															className={BTN_DANGER}
															onClick={() => handleDelete(row)}
															disabled={
																deleteMutation.isPending &&
																deleteMutation.variables === row.id
															}
														>
															Delete
														</button>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</section>

				<section
					className={`${CARD} lg:col-span-2`}
					aria-label="Template preview"
					data-testid="saved-report-templates-preview"
				>
					<div className="border-b border-zinc-100 px-4 py-3">
						<h2 className="text-sm font-semibold text-zinc-900">Preview</h2>
					</div>

					{formMode.kind !== "hidden" ? (
						<NewTemplateForm
							mode={formMode}
							isSubmitting={createMutation.isPending}
							errorMessage={
								createMutation.error ? createMutation.error.message : null
							}
							onCancel={() => setFormMode({ kind: "hidden" })}
							onSubmit={async (body) => {
								const created = await createMutation.mutateAsync(body);
								setFormMode({ kind: "hidden" });
								setSelectedId(created.id);
							}}
						/>
					) : selected ? (
						<TemplatePreview template={selected} />
					) : (
						<p
							className="px-4 py-12 text-center text-sm text-zinc-500"
							data-testid="saved-report-templates-preview-empty"
						>
							Select a template to see its last run summary, or create a new
							one.
						</p>
					)}
				</section>
			</div>
		</main>
	);
}

function TemplatePreview({
	template,
}: {
	template: SavedReportTemplateRow;
}) {
	const def = template.definition ?? {};
	const groupBy = Array.isArray(def.group_by) ? def.group_by : [];
	const columns = Array.isArray(def.columns) ? def.columns : [];
	const metrics = Array.isArray(def.metrics) ? def.metrics : [];

	return (
		<div className="flex flex-col gap-4 px-4 py-4">
			<div>
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					Template
				</p>
				<p className="text-base font-semibold text-zinc-900">
					{template.name}
				</p>
			</div>

			<dl className="grid grid-cols-2 gap-3 text-sm">
				<div>
					<dt className="text-xs text-zinc-500">Report type</dt>
					<dd className="text-zinc-900">{def.report_type ?? "—"}</dd>
				</div>
				<div>
					<dt className="text-xs text-zinc-500">Date range</dt>
					<dd className="text-zinc-900">{def.date_range ?? "—"}</dd>
				</div>
				<div>
					<dt className="text-xs text-zinc-500">Created by</dt>
					<dd className="text-zinc-900">{template.created_by ?? "—"}</dd>
				</div>
				<div>
					<dt className="text-xs text-zinc-500">Last run</dt>
					<dd className="text-zinc-900">
						{relativeTime(template.last_run_at)}
					</dd>
				</div>
				<div className="col-span-2">
					<dt className="text-xs text-zinc-500">Schedule</dt>
					<dd className="text-zinc-900">{def.schedule ?? "On demand"}</dd>
				</div>
			</dl>

			<div className="flex flex-col gap-2">
				<div>
					<p className="text-xs text-zinc-500">Grouped by</p>
					<p className="text-sm text-zinc-900">
						{groupBy.length > 0 ? groupBy.join(", ") : "—"}
					</p>
				</div>
				<div>
					<p className="text-xs text-zinc-500">Metrics</p>
					<p className="text-sm text-zinc-900">
						{metrics.length > 0 ? metrics.join(", ") : "—"}
					</p>
				</div>
				<div>
					<p className="text-xs text-zinc-500">Columns</p>
					<p className="text-sm text-zinc-900">
						{columns.length > 0 ? columns.join(", ") : "—"}
					</p>
				</div>
			</div>
		</div>
	);
}

function NewTemplateForm({
	mode,
	isSubmitting,
	errorMessage,
	onCancel,
	onSubmit,
}: {
	mode: FormMode;
	isSubmitting: boolean;
	errorMessage: string | null;
	onCancel: () => void;
	onSubmit: (body: {
		name: string;
		report_type: string;
		date_range: string;
		filters: Record<string, unknown>;
		group_by: string[];
		columns: string[];
		schedule: string | null;
	}) => void | Promise<void>;
}) {
	const base = mode.kind === "hidden" ? null : "template" in mode ? mode.template : null;
	const seedName =
		mode.kind === "duplicate" && base
			? `${base.name} (copy)`
			: base
				? base.name
				: "";
	const seedType = base?.definition?.report_type ?? "occupancy";
	const seedRange = base?.definition?.date_range ?? "last_30_days";
	const seedGroupBy = (base?.definition?.group_by ?? []).join(", ");
	const seedColumns = (base?.definition?.columns ?? []).join(", ");
	const seedSchedule = base?.definition?.schedule ?? "";
	const seedFilters =
		base?.definition?.filters && typeof base.definition.filters === "object"
			? JSON.stringify(base.definition.filters, null, 2)
			: "{}";

	const [name, setName] = useState(seedName);
	const [reportType, setReportType] = useState(seedType);
	const [dateRange, setDateRange] = useState(seedRange);
	const [groupBy, setGroupBy] = useState(seedGroupBy);
	const [columns, setColumns] = useState(seedColumns);
	const [schedule, setSchedule] = useState(seedSchedule ?? "");
	const [filters, setFilters] = useState(seedFilters);
	const [filterError, setFilterError] = useState<string | null>(null);

	const title =
		mode.kind === "new"
			? "New template"
			: mode.kind === "duplicate"
				? "Duplicate template"
				: "Edit template";

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		let parsedFilters: Record<string, unknown> = {};
		const trimmedFilters = filters.trim();
		if (trimmedFilters) {
			try {
				const parsed = JSON.parse(trimmedFilters);
				if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
					parsedFilters = parsed as Record<string, unknown>;
					setFilterError(null);
				} else {
					setFilterError("Filters must be a JSON object");
					return;
				}
			} catch {
				setFilterError("Filters must be valid JSON");
				return;
			}
		} else {
			setFilterError(null);
		}
		await onSubmit({
			name: name.trim(),
			report_type: reportType.trim(),
			date_range: dateRange.trim(),
			filters: parsedFilters,
			group_by: groupBy
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			columns: columns
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			schedule: schedule.trim() ? schedule.trim() : null,
		});
	}

	return (
		<form
			className="flex flex-col gap-3 px-4 py-4"
			onSubmit={handleSubmit}
			data-testid="saved-report-templates-form"
			aria-label={title}
		>
			<div className="flex items-center justify-between">
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					{title}
				</p>
				<button
					type="button"
					className={BTN_GHOST}
					onClick={onCancel}
					disabled={isSubmitting}
				>
					Cancel
				</button>
			</div>

			<label className="flex flex-col gap-1 text-sm text-zinc-700">
				<span>Name</span>
				<input
					required
					value={name}
					onChange={(e) => setName(e.target.value)}
					className={INPUT}
					data-testid="saved-report-templates-form-name"
				/>
			</label>

			<div className="grid grid-cols-2 gap-3">
				<label className="flex flex-col gap-1 text-sm text-zinc-700">
					<span>Report type</span>
					<input
						value={reportType}
						onChange={(e) => setReportType(e.target.value)}
						className={INPUT}
						placeholder="occupancy"
					/>
				</label>
				<label className="flex flex-col gap-1 text-sm text-zinc-700">
					<span>Default date range</span>
					<input
						value={dateRange}
						onChange={(e) => setDateRange(e.target.value)}
						className={INPUT}
						placeholder="last_30_days"
					/>
				</label>
			</div>

			<label className="flex flex-col gap-1 text-sm text-zinc-700">
				<span>Group by (comma separated)</span>
				<input
					value={groupBy}
					onChange={(e) => setGroupBy(e.target.value)}
					className={INPUT}
					placeholder="channel, room_type"
				/>
			</label>

			<label className="flex flex-col gap-1 text-sm text-zinc-700">
				<span>Columns (comma separated)</span>
				<input
					value={columns}
					onChange={(e) => setColumns(e.target.value)}
					className={INPUT}
					placeholder="revenue, nights, adr"
				/>
			</label>

			<label className="flex flex-col gap-1 text-sm text-zinc-700">
				<span>Filters (JSON object)</span>
				<textarea
					value={filters}
					onChange={(e) => setFilters(e.target.value)}
					className={`${INPUT} min-h-[6rem] font-mono text-xs`}
				/>
				{filterError ? (
					<span className="text-xs text-red-600" role="alert">
						{filterError}
					</span>
				) : null}
			</label>

			<label className="flex flex-col gap-1 text-sm text-zinc-700">
				<span>Schedule (optional cron or keyword)</span>
				<input
					value={schedule}
					onChange={(e) => setSchedule(e.target.value)}
					className={INPUT}
					placeholder="weekly"
				/>
			</label>

			{errorMessage ? (
				<p className="text-xs text-red-600" role="alert">
					{errorMessage}
				</p>
			) : null}

			<div className="flex items-center justify-end gap-2 pt-2">
				<button
					type="button"
					className={BTN_GHOST}
					onClick={onCancel}
					disabled={isSubmitting}
				>
					Discard
				</button>
				<button
					type="submit"
					className={BTN_PRIMARY}
					disabled={isSubmitting || !name.trim()}
					data-testid="saved-report-templates-form-submit"
				>
					{isSubmitting ? "Saving…" : "Save template"}
				</button>
			</div>
		</form>
	);
}

export default SavedReportTemplatesPage;
